import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { PlatformHeader } from "@/components/abigail/PlatformHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useAuthActions, useMe } from "@/lib/auth";
import { PASSWORD_MIN_LENGTH, validateStrongPassword } from "@shared/password";

export const Route = createFileRoute("/_authenticated/perfil")({
  component: ProfilePage,
});

function ProfilePage() {
  const me = useMe();
  const { refreshMe } = useAuthActions();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<{ type: "ok" | "erro"; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setStatus(null);

    const check = validateStrongPassword(password, confirm);
    if (!check.ok) {
      setStatus({ type: "erro", message: check.message ?? "Senha inválida." });
      return;
    }

    setSaving(true);
    try {
      await api.post("/api/auth/password", { password });
      setPassword("");
      setConfirm("");
      setStatus({ type: "ok", message: "Senha atualizada." });
      await refreshMe();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Não foi possível trocar a senha agora.";
      setStatus({ type: "erro", message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col">
      <AbigailBackdrop dim />
      <PlatformHeader />

      <main className="flex flex-1 items-start justify-center px-4 py-10">
        <section className="w-full max-w-md rounded-2xl border border-border bg-card px-7 py-8 shadow-card-deep backdrop-blur-md">
          <h1 className="font-serif text-ember text-3xl">Minha conta</h1>

          <dl className="mt-6 space-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Nome</dt>
              <dd className="text-foreground">{me.data?.fullName ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">E-mail de acesso</dt>
              <dd className="text-foreground">{me.data?.email ?? "—"}</dd>
            </div>
          </dl>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <h2 className="text-sm font-medium text-foreground">Trocar senha</h2>
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-xs text-muted-foreground">
                Nova senha
              </Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                maxLength={200}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 border-border bg-background/60 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-xs text-muted-foreground">
                Confirmar nova senha
              </Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                maxLength={200}
                onChange={(event) => setConfirm(event.target.value)}
                className="h-11 border-border bg-background/60 text-sm"
              />
            </div>

            <p className="text-muted-foreground text-xs">
              Mínimo de {PASSWORD_MIN_LENGTH} caracteres, com maiúscula, minúscula, número e
              caractere especial.
            </p>

            {status ? (
              <p
                role="alert"
                className={status.type === "ok" ? "text-xs text-accent" : "text-xs text-destructive"}
              >
                {status.message}
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={saving}
              className="bg-ember shadow-ember h-11 w-full text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nova senha"}
            </Button>
          </form>
        </section>
      </main>
    </div>
  );
}
